import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';
import { coreCall } from '../services/gatewayService';
import { isMetaConversationUnread } from '../utils/metaUnread';

/** Codice stabile dell'errore del core, se coreCall lo riporta (dipende dalla versione di gatewayService). */
function coreErrorCode(error: { message: string }): string | null {
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

export interface MetaConversation {
  id: string;
  organization_id: string;
  channel: 'instagram' | 'messenger';
  page_id: string;
  external_contact_id: string;
  contact_name: string | null;
  last_message_at: string | null;
  // Ultimo messaggio IN ARRIVO: da qui decorre la finestra di risposta Meta
  // (24 ore, 7 giorni con tag operatore umano). NULL = mai ricevuto → bloccata.
  last_inbound_at?: string | null;
  // Aggancio manuale a lead o cliente (migration 20260902), scritto solo via RPC.
  client_id?: string | null;
  // Profilo dalla Graph API (core, al primo messaggio in arrivo e ogni 2 giorni).
  profile_pic_url?: string | null;
  profile_username?: string | null;
  profile_fetched_at?: string | null;
  // Anteprima dell'ultimo messaggio (trigger su meta_messages, migration 20260925c).
  last_message_preview?: string | null;
  last_message_direction?: 'inbound' | 'outbound' | null;
  last_message_preview_at?: string | null;
  // Da dove e' arrivato il contatto: link m.me / ig.me con ref, pubblicita', QR (migration 20260925c).
  referral_ref?: string | null;
  referral_source?: string | null;
  referral_type?: string | null;
  referral_at?: string | null;
}

export type MetaMessageStatus = 'sending' | 'failed';

export interface MetaAttachment {
  type: string;
  url: string | null;
  /** Copia nello storage del CRM (bucket privato): l'URL Meta scade dopo poche ore. */
  storage_path?: string | null;
  mime_type?: string | null;
}

export interface MetaMessage {
  id: string;
  conversation_id: string;
  direction: 'inbound' | 'outbound';
  content: string;
  // Foto, vocali, video e reel dei DM: { type, url } da message.attachments[] del webhook
  // Meta. L'URL firmato scade dopo poche ore, il type resta sempre significativo.
  attachments: MetaAttachment[];
  sent_at: string;
  /** mid Meta (dopo l'invio o dal webhook): serve a trovare il messaggio citato. */
  meta_message_id?: string | null;
  // Spunte: consegnato / letto dal contatto (webhook message_deliveries, message_reads, messaging_seen).
  delivered_at?: string | null;
  read_at?: string | null;
  // mid Meta del messaggio citato (risposta a un messaggio preciso) e reazione messa dal CRM.
  reply_to_mid?: string | null;
  our_reaction?: string | null;
  // Eventi del contatto (migration 20260925c): reazione, modifica, cancellazione, contenuto
  // non supportato da Instagram, risposta a una storia e pulsante / Ice Breaker premuto.
  contact_reaction?: string | null;
  edited_at?: string | null;
  deleted_at?: string | null;
  is_unsupported?: boolean | null;
  story_reply?: { url?: string | null; id?: string | null } | null;
  postback_payload?: string | null;
  // Solo lato client: bolla ottimistica in invio, o fallita (con riprova).
  status?: MetaMessageStatus;
  error?: string;
  /** Solo lato client: file da mandare (resta qui per la riprova). */
  localFile?: File;
  /** Solo lato client: percorso nello storage dopo il caricamento (la riprova non ricarica). */
  uploadedPath?: string;
  /** Solo lato client: il file e' gia' arrivato al contatto, la riprova manda solo il testo. */
  attachmentSent?: boolean;
}

/** Libera l'URL locale dell'anteprima (blob:) di una bolla ottimistica. */
function revokeLocalPreview(message: MetaMessage | undefined): void {
  for (const a of message?.attachments ?? []) {
    if (a.url && a.url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(a.url);
      } catch {
        /* ambiente senza URL.revokeObjectURL (test) */
      }
    }
  }
}

/** Tipo Meta dell'allegato dal mimetype (doc Send API: image, video, audio, file=PDF). */
export function metaAttachmentType(mime: string): 'image' | 'video' | 'audio' | 'file' {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'file';
}

/** Limiti Meta per tipo (doc Instagram Messaging API): foto 8 MB, il resto 25 MB; bucket 16 MB. */
export function metaAttachmentTooBig(file: File): boolean {
  const limit = metaAttachmentType(file.type) === 'image' ? 8 : 16;
  return file.size > limit * 1024 * 1024;
}

const SEND_MESSAGE_ERROR_MESSAGES: Record<string, string> = {
  window_expired: 'Finestra di risposta scaduta',
  messaging_disabled: 'Messaggistica non attiva per questa organizzazione',
  messaging_flag_unavailable: 'Servizio momentaneamente non disponibile, riprova',
  page_token_missing: 'Pagina Facebook non collegata',
  // 503 dal core: la lettura di ad_account_configs è fallita, la config esiste ma non è
  // leggibile ora. Diverso da page_token_missing (409), che significa "non collegata".
  page_token_unavailable: 'Configurazione Meta momentaneamente non disponibile, riprova tra poco',
  // Inbox per utente: l'utente non è assegnato alla pagina di questa conversazione.
  page_not_allowed: 'Non hai accesso alla pagina di questa conversazione',
  page_access_unavailable: 'Verifica degli accessi momentaneamente non disponibile, riprova',
  text_too_long: 'Messaggio troppo lungo: massimo 1000 byte (accenti ed emoji contano di più)',
  empty_message: 'Scrivi un messaggio o allega un file',
  attachment_invalid: 'Allegato non valido: usa foto, video, audio o PDF',
  reply_to_invalid: 'Il messaggio a cui rispondi non è più disponibile: annulla la risposta e riprova',
  body_invalid: 'Messaggio non valido, riprova',
  text_invalid: 'Testo del messaggio non valido, riprova',
  // Allegato + testo: il file e' arrivato al contatto, il testo no.
  partial_attachment_sent: 'Il file è stato inviato, il testo no: premi Riprova per mandare solo il testo',
  attachment_path_invalid: 'Allegato non valido, riprova a caricarlo',
  attachment_not_found: 'Allegato non trovato, riprova a caricarlo',
  attachment_upload_failed: 'Caricamento del file non riuscito, riprova',
  attachment_too_big: 'File troppo grande: foto fino a 8 MB, altri file fino a 16 MB',
  graph_api_error: 'Meta ha rifiutato il messaggio',
  // 401 dal core (JWT scaduto o non verificabile): non e' un problema del messaggio.
  Unauthorized: 'Sessione non valida: ricarica la pagina e riprova',
  JWT: 'Sessione non valida: ricarica la pagina e riprova',
  'HTTP 401': 'Sessione non valida: ricarica la pagina e riprova',
  forbidden: 'Non hai il permesso di inviare messaggi',
  // Il DM è partito verso il cliente ma il core non è riuscito a scriverlo in cronologia:
  // non è un invio fallito, ma l'agente non ritroverà il messaggio nel thread.
  outbound_not_persisted: 'Messaggio inviato, ma non è stato possibile salvarlo nella cronologia',
};

function mapSendMessageError(message: string | undefined | null, code?: string | null): string {
  if (code && SEND_MESSAGE_ERROR_MESSAGES[code]) return SEND_MESSAGE_ERROR_MESSAGES[code];
  if (!message) return 'Invio fallito';
  const key = Object.keys(SEND_MESSAGE_ERROR_MESSAGES).find((k) => message.includes(k));
  return key ? SEND_MESSAGE_ERROR_MESSAGES[key] : 'Invio fallito';
}

// Chiave di ordinamento del thread: la stessa di fetchMessages (`sent_at`), con `created_at`
// come ripiego per le righe realtime che non lo portassero.
function messageTime(message: MetaMessage & { created_at?: string }): number {
  const value = message.sent_at ?? message.created_at;
  return value ? new Date(value).getTime() : 0;
}

function isTempId(id: string): boolean {
  return id.startsWith('temp-');
}

// Append di una riga arrivata dal realtime al thread già in memoria: dedup per `id`
// (Supabase può riconsegnare lo stesso evento) e ordine cronologico mantenuto.
function appendRealtimeMessage(
  set: (fn: (state: MetaMessagingStore) => Partial<MetaMessagingStore>) => void,
  row: any
): void {
  const conversationId: string | undefined = row?.conversation_id;
  if (!conversationId || !row?.id) return;
  set((state) => {
    const thread = state.messagesByConversation[conversationId];
    // Thread non aperto: non lo si crea a metà, lo caricherà fetchMessages per intero.
    if (!thread) return {};
    if (thread.some((m) => m.id === row.id)) return {};
    const message: MetaMessage = { ...row, attachments: Array.isArray(row.attachments) ? row.attachments : [] };
    const next = [...thread, message].sort((a, b) => messageTime(a) - messageTime(b));
    return { messagesByConversation: { ...state.messagesByConversation, [conversationId]: next } };
  });
}

// Aggiornamento di una riga (spunte consegnato/letto, allegati salvati): sostituisce in posto.
function updateRealtimeMessage(
  set: (fn: (state: MetaMessagingStore) => Partial<MetaMessagingStore>) => void,
  row: any
): void {
  const conversationId: string | undefined = row?.conversation_id;
  if (!conversationId || !row?.id) return;
  set((state) => {
    const thread = state.messagesByConversation[conversationId];
    if (!thread || !thread.some((m) => m.id === row.id)) return {};
    const next = thread.map((m) =>
      m.id === row.id ? { ...m, ...row, attachments: Array.isArray(row.attachments) ? row.attachments : m.attachments } : m
    );
    return { messagesByConversation: { ...state.messagesByConversation, [conversationId]: next } };
  });
}

interface MetaMessagingStore {
  conversations: MetaConversation[];
  messagesByConversation: Record<string, MetaMessage[]>;
  loading: boolean;
  /** Prima lettura fatta: distingue "vuoto" da "non ancora caricato" (scheletri). */
  loaded: boolean;
  error: string | null;
  /**
   * Errori d'invio per conversazione: un allegato che fallisce mentre si e' passati a
   * un'altra chat deve restare sotto la sua conversazione, non comparire in quella aperta.
   */
  sendErrors: Record<string, string>;
  /** Scrive (o toglie con null) l'errore d'invio di una conversazione. */
  setSendError: (conversationId: string, message: string | null) => void;
  /** Conversazione aperta nel thread (per canale: chi apre Instagram non vede quella di Messenger). */
  selectedConversationId: string | null;
  /**
   * Pagine Facebook a cui l'utente corrente è assegnato (`meta_page_user_access`).
   * `null` = non ancora caricato. Serve solo alla UI (selettore canale, pulsante
   * di gestione): la visibilità vera la fa la RLS.
   */
  pageAccess: string[] | null;
  /** "Letto da me": conversationId → last_read_at (meta_conversation_reads). */
  reads: Record<string, string>;

  fetchConversations: () => Promise<void>;
  fetchMessages: (conversationId: string) => Promise<void>;
  sendMessage: (conversationId: string, text: string, options?: { replyToMid?: string | null; file?: File | null }) => Promise<boolean>;
  /** Messaggio a cui si sta rispondendo, per conversazione (barra "Rispondi a"). */
  replyTo: Record<string, MetaMessage | null>;
  setReplyTo: (conversationId: string, message: MetaMessage | null) => void;
  /**
   * Testo da mettere nel campo di scrittura (bozza dell'assistente, "In chat"
   * dal pannello laterale). Il composer lo prende e lo svuota; non viene inviato.
   */
  pendingDraft: Record<string, string>;
  setPendingDraft: (conversationId: string, text: string | null) => void;
  /** Reazione dell'azienda a un messaggio del contatto (null = togli). */
  reactToMessage: (conversationId: string, messageId: string, reaction: string | null) => Promise<boolean>;
  /** Rimanda un messaggio fallito (bolla rossa "Riprova"). */
  retryMessage: (conversationId: string, tempId: string) => Promise<boolean>;
  discardFailedMessage: (conversationId: string, tempId: string) => void;
  subscribeRealtime: () => () => void;
  selectConversation: (conversationId: string | null) => void;
  fetchPageAccess: () => Promise<void>;
  fetchReads: () => Promise<void>;
  /** Segna la conversazione come letta da me (ora). */
  markRead: (conversationId: string) => Promise<void>;
  /** Ha messaggi in arrivo successivi all'ultima lettura (o mai letta). */
  isUnread: (conversation: MetaConversation) => boolean;
  /** Collega (o scollega con null) la conversazione a un lead/cliente. Ritorna false su errore. */
  linkClient: (conversationId: string, clientId: string | null) => Promise<boolean>;
  /** "Visualizzato" (all'apertura) e "sta scrivendo" (limitato a uno ogni 20 s) verso il contatto. */
  sendSenderAction: (conversationId: string, action: 'mark_seen' | 'typing_on') => Promise<void>;
  /** Toglie l'errore generale e, se indicata, quello d'invio della conversazione. */
  clearError: (conversationId?: string) => void;
}

export const useMetaMessagingStore = create<MetaMessagingStore>((set, get) => ({
  conversations: [],
  messagesByConversation: {},
  replyTo: {},
  pendingDraft: {},
  loading: false,
  loaded: false,
  error: null,
  sendErrors: {},
  selectedConversationId: null,
  pageAccess: null,
  reads: {},

  fetchConversations: async () => {
    set({ loading: true, error: null });
    try {
      // Filtro org esplicito: la RLS resta la difesa, ma per un superadmin la sua policy
      // è permissiva su TUTTE le org e senza questo filtro vedrebbe i DM di tutti i
      // tenant mescolati in un'unica lista. Qui si mostra solo l'org corrente.
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase
        .from('meta_conversations')
        .select('*')
        .eq('organization_id', organizationId)
        .order('last_message_at', { ascending: false });
      if (error) throw error;
      set({ conversations: (data as MetaConversation[]) ?? [], loaded: true });
    } catch (err: any) {
      set({ error: err.message ?? 'Errore nel caricamento delle conversazioni' });
    } finally {
      set({ loading: false });
    }
  },

  fetchMessages: async (conversationId: string) => {
    // Stesso filtro org di fetchConversations (UX superadmin, vedi commento lì).
    let organizationId: string;
    try {
      organizationId = await getCurrentUserOrganizationId();
    } catch (err: any) {
      set({ error: err?.message ?? 'Organizzazione non determinata' });
      return;
    }
    const { data, error } = await supabase
      .from('meta_messages')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('conversation_id', conversationId)
      .order('sent_at', { ascending: true });
    if (error) {
      set({ error: error.message });
      return;
    }
    set((state) => {
      // Le bolle ottimistiche ancora in corso (o fallite) restano in coda al thread ricaricato.
      const pending = (state.messagesByConversation[conversationId] ?? []).filter((m) => isTempId(m.id));
      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: [...((data as MetaMessage[]) ?? []), ...pending],
        },
      };
    });
  },

  sendMessage: async (conversationId: string, text: string, options) => {
    get().setSendError(conversationId, null);
    const file = options?.file ?? null;
    if (file && metaAttachmentTooBig(file)) {
      get().setSendError(conversationId, SEND_MESSAGE_ERROR_MESSAGES.attachment_too_big);
      return false;
    }
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic: MetaMessage = {
      id: tempId,
      conversation_id: conversationId,
      direction: 'outbound',
      content: text,
      // Anteprima locale del file finche' non arriva la riga vera dal DB.
      attachments: file ? [{ type: metaAttachmentType(file.type), url: URL.createObjectURL(file) }] : [],
      reply_to_mid: options?.replyToMid ?? null,
      sent_at: new Date().toISOString(),
      status: 'sending',
      ...(file ? { localFile: file } : {}),
    };
    // La barra "Rispondi a" si chiude sempre all'invio (anche se il messaggio citato non ha mid).
    set((state) => ({ replyTo: { ...state.replyTo, [conversationId]: null } }));
    set((state) => ({
      messagesByConversation: {
        ...state.messagesByConversation,
        [conversationId]: [...(state.messagesByConversation[conversationId] ?? []), optimistic],
      },
    }));
    return get().retryMessage(conversationId, tempId);
  },

  retryMessage: async (conversationId, tempId) => {
    const current = (get().messagesByConversation[conversationId] ?? []).find((m) => m.id === tempId);
    if (!current) return false;
    const text = current.content;
    const patch = (changes: Partial<MetaMessage>) =>
      set((state) => ({
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: (state.messagesByConversation[conversationId] ?? []).map((m) =>
            m.id === tempId ? { ...m, ...changes } : m
          ),
        },
      }));
    const remove = () =>
      set((state) => ({
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: (state.messagesByConversation[conversationId] ?? []).filter((m) => m.id !== tempId),
        },
      }));
    patch({ status: 'sending', error: undefined });
    try {
      // Allegato: si carica una volta sola nella cartella di uscita dell'org, poi il core
      // crea il link firmato per Meta. Alla riprova si riusa il percorso gia' caricato.
      let uploadedPath = current.uploadedPath;
      // File gia' arrivato al contatto (invio parziale): la riprova manda solo il testo.
      const file = current.attachmentSent ? undefined : current.localFile;
      if (file && !uploadedPath) {
        const organizationId = await getCurrentUserOrganizationId();
        const ext = (file.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8) || 'bin';
        const path = `${organizationId}/outbound/${conversationId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: upErr } = await supabase.storage.from('meta-media').upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) {
          const friendly = SEND_MESSAGE_ERROR_MESSAGES.attachment_upload_failed;
          patch({ status: 'failed', error: friendly });
          get().setSendError(conversationId, friendly);
          return false;
        }
        uploadedPath = path;
        patch({ uploadedPath: path });
      }
      const body: Record<string, unknown> = {};
      if (text.trim()) body.text = text;
      if (file && uploadedPath) body.attachment = { storage_path: uploadedPath, type: metaAttachmentType(file.type), mime_type: file.type || undefined };
      if (current.reply_to_mid) body.reply_to_mid = current.reply_to_mid;
      const { data, error } = await coreCall<{
        ok: boolean;
        window?: string;
        persisted?: boolean;
        warning?: string;
      }>(`meta-messaging/conversations/${conversationId}/send`, { body });
      if (error) {
        // Invio parziale: il core dice (code partial_attachment_sent) che il file e' partito e
        // il testo no. Per sicurezza lo si verifica anche nel thread ricaricato: se la riga del
        // file caricato c'e', il file non va rimandato.
        const errorCode = coreErrorCode(error);
        let fileSent = errorCode === 'partial_attachment_sent';
        if (file && uploadedPath && !fileSent) {
          await get().fetchMessages(conversationId);
          fileSent = (get().messagesByConversation[conversationId] ?? []).some(
            (m) => !isTempId(m.id) && m.attachments?.some((a) => a.storage_path === uploadedPath)
          );
        } else if (fileSent) {
          await get().fetchMessages(conversationId);
        }
        if (fileSent && file) {
          if (!text.trim()) {
            remove();
            return true;
          }
          revokeLocalPreview(current);
          const friendly = SEND_MESSAGE_ERROR_MESSAGES.partial_attachment_sent;
          patch({ status: 'failed', error: friendly, attachmentSent: true, attachments: [], localFile: undefined, reply_to_mid: null });
          get().setSendError(conversationId, friendly);
          return false;
        }
        const friendly = mapSendMessageError(error.message, errorCode);
        patch({ status: 'failed', error: friendly });
        get().setSendError(conversationId, friendly);
        return false;
      }
      // La riga vera arriva dal DB: il refetch sostituisce la bolla ottimistica.
      revokeLocalPreview(current);
      remove();
      await get().fetchMessages(conversationId);
      // 200 con persisted:false → il messaggio è uscito davvero, quindi conta come inviato,
      // ma va detto all'utente che nel thread non lo troverà (fetchMessages non lo vedrà mai).
      if (data && (data.persisted === false || data.warning === 'outbound_not_persisted')) {
        get().setSendError(conversationId, SEND_MESSAGE_ERROR_MESSAGES.outbound_not_persisted);
      }
      return true;
    } catch (err: any) {
      const friendly = err?.message ?? 'Invio fallito';
      patch({ status: 'failed', error: friendly });
      get().setSendError(conversationId, friendly);
      return false;
    }
  },

  discardFailedMessage: (conversationId, tempId) => {
    revokeLocalPreview((get().messagesByConversation[conversationId] ?? []).find((m) => m.id === tempId));
    set((state) => ({
      messagesByConversation: {
        ...state.messagesByConversation,
        [conversationId]: (state.messagesByConversation[conversationId] ?? []).filter((m) => m.id !== tempId),
      },
    }));
  },

  subscribeRealtime: () => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;

    (async () => {
      try {
        const organizationId = await getCurrentUserOrganizationId();
        if (cancelled) return;

        channel = supabase
          .channel(`meta_messaging_${organizationId}`)
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'meta_messages', filter: `organization_id=eq.${organizationId}` },
            (payload: any) => {
              // Il refetch delle conversazioni riordina la sidebar, ma da solo lascia fermo il
              // thread aperto: un DM in arrivo non comparirebbe finché non si cambia chat.
              // Qui il messaggio nuovo viene appeso al thread se è già caricato; un UPDATE
              // (spunte consegnato/letto, allegati salvati) sostituisce la riga in posto.
              if (payload?.eventType === 'INSERT') appendRealtimeMessage(set, payload.new);
              else if (payload?.eventType === 'UPDATE') updateRealtimeMessage(set, payload.new);
              get().fetchConversations().catch(() => undefined);
            }
          )
          .subscribe();
      } catch (err) {
        console.warn('[metaMessaging] subscribeRealtime failed (non-fatal):', err);
      }
    })();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  },

  selectConversation: (conversationId) => {
    set({ selectedConversationId: conversationId, error: null });
    if (!conversationId) return;
    if (!get().messagesByConversation[conversationId]) {
      get().fetchMessages(conversationId).catch(() => undefined);
    }
    get().markRead(conversationId).catch(() => undefined);
  },

  fetchPageAccess: async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) {
        set({ pageAccess: [] });
        return;
      }
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase
        .from('meta_page_user_access')
        .select('page_id')
        .eq('organization_id', organizationId)
        .eq('user_id', userId);
      if (error) throw error;
      set({ pageAccess: (data ?? []).map((row: { page_id: string }) => row.page_id) });
    } catch (err) {
      console.warn('[metaMessaging] fetchPageAccess failed (non-fatal):', err);
      set({ pageAccess: [] });
    }
  },

  fetchReads: async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) return;
      const organizationId = await getCurrentUserOrganizationId();
      const { data, error } = await supabase
        .from('meta_conversation_reads')
        .select('conversation_id, last_read_at')
        .eq('organization_id', organizationId)
        .eq('user_id', userId);
      if (error) throw error;
      const reads: Record<string, string> = {};
      for (const row of (data ?? []) as Array<{ conversation_id: string; last_read_at: string }>) {
        reads[row.conversation_id] = row.last_read_at;
      }
      set({ reads });
    } catch (err) {
      console.warn('[metaMessaging] fetchReads failed (non-fatal):', err);
    }
  },

  markRead: async (conversationId) => {
    const conversation = get().conversations.find((c) => c.id === conversationId);
    // L'ultimo messaggio in arrivo puo' essere gia' nella lista messaggi prima che la riga
    // conversazione (last_inbound_at) sia aggiornata dal realtime: si prende il piu' recente
    // dei due. Prima si guardava solo la conversazione, si concludeva "gia' letta" e il
    // badge restava acceso appena arrivava l'aggiornamento (visto su Instagram il 25/09).
    const inboundTimes = [
      conversation?.last_inbound_at,
      ...(get().messagesByConversation[conversationId] ?? [])
        .filter((m) => m.direction === 'inbound')
        .map((m) => m.sent_at),
    ]
      .filter(Boolean)
      .map((t) => new Date(t as string).getTime());
    const latestInbound = inboundTimes.length ? Math.max(...inboundTimes) : 0;
    const last = get().reads[conversationId];
    if (last && latestInbound && new Date(last).getTime() >= latestInbound) return;
    // Mai prima dell'ultimo messaggio: l'ora di Meta puo' essere avanti rispetto al browser.
    const now = new Date(Math.max(Date.now(), latestInbound)).toISOString();
    set((state) => ({ reads: { ...state.reads, [conversationId]: now } }));
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) return;
      const organizationId = conversation?.organization_id ?? (await getCurrentUserOrganizationId());
      await supabase
        .from('meta_conversation_reads')
        .upsert(
          { organization_id: organizationId, conversation_id: conversationId, user_id: userId, last_read_at: now },
          { onConflict: 'conversation_id,user_id' }
        );
    } catch (err) {
      console.warn('[metaMessaging] markRead failed (non-fatal):', err);
    }
  },

  // Regola in utils/metaUnread: se la nostra ultima risposta (anche dall'app di Meta) e'
  // piu' recente del messaggio del contatto, la conversazione non conta come non letta.
  isUnread: (conversation) => isMetaConversationUnread(conversation, get().reads[conversation.id]),

  linkClient: async (conversationId, clientId) => {
    set({ error: null });
    const { error } = await supabase.rpc('link_meta_conversation_client', {
      p_conversation_id: conversationId,
      p_client_id: clientId,
    });
    if (error) {
      const msg = error.message ?? '';
      set({
        error: msg.includes('page_not_allowed')
          ? SEND_MESSAGE_ERROR_MESSAGES.page_not_allowed
          : msg.includes('client_not_in_organization')
            ? 'Il cliente scelto non appartiene a questa organizzazione'
            : msg.includes('forbidden')
              ? 'Non hai il permesso di collegare le conversazioni'
              : 'Collegamento non riuscito',
      });
      return false;
    }
    set((state) => ({
      conversations: state.conversations.map((c) => (c.id === conversationId ? { ...c, client_id: clientId } : c)),
    }));
    return true;
  },

  setReplyTo: (conversationId, message) =>
    set((state) => ({ replyTo: { ...state.replyTo, [conversationId]: message } })),

  setPendingDraft: (conversationId, text) =>
    set((state) => {
      const next = { ...state.pendingDraft };
      if (text) next[conversationId] = text;
      else delete next[conversationId];
      return { pendingDraft: next };
    }),

  reactToMessage: async (conversationId, messageId, reaction) => {
    const patchReaction = (value: string | null | undefined) =>
      set((state) => ({
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: (state.messagesByConversation[conversationId] ?? []).map((m) =>
            m.id === messageId ? { ...m, our_reaction: value ?? null } : m
          ),
        },
      }));
    const before = (get().messagesByConversation[conversationId] ?? []).find((m) => m.id === messageId)?.our_reaction ?? null;
    patchReaction(reaction);
    const { data, error } = await coreCall<{ reaction?: string | null; persisted?: boolean }>(
      `meta-messaging/messages/${messageId}/react`,
      { body: { reaction } }
    );
    if (error) {
      patchReaction(before);
      get().setSendError(conversationId, 'Reazione non inviata: ' + mapSendMessageError(error.message, coreErrorCode(error)).toLowerCase());
      return false;
    }
    // Instagram accetta solo il cuore: il core restituisce la reazione davvero messa.
    if (data && 'reaction' in data) patchReaction(data.reaction ?? null);
    if (data?.persisted === false) get().setSendError(conversationId, 'Reazione inviata, ma non salvata nel CRM');
    return true;
  },

  sendSenderAction: async (conversationId, action) => {
    // Cosmetica: "visualizzato" e "sta scrivendo" verso il contatto. Nessun errore a schermo.
    if (action === 'typing_on') {
      const now = Date.now();
      if (!shouldSendTypingNow(lastTypingSent.get(conversationId), now)) return;
      lastTypingSent.set(conversationId, now);
    }
    try {
      await coreCall(`meta-messaging/conversations/${conversationId}/sender-action`, { body: { action } });
    } catch {
      /* best effort */
    }
  },

  setSendError: (conversationId, message) =>
    set((state) => {
      if (!message && !(conversationId in state.sendErrors)) return {};
      const next = { ...state.sendErrors };
      if (message) next[conversationId] = message;
      else delete next[conversationId];
      return { sendErrors: next };
    }),

  clearError: (conversationId) => {
    set({ error: null });
    if (conversationId) get().setSendError(conversationId, null);
  },
}));

/** Ultimo "sta scrivendo" mandato per conversazione (stesso limite del core: 20 s). */
const lastTypingSent = new Map<string, number>();
const TYPING_INTERVAL_MS = 20_000;
export function shouldSendTypingNow(lastSentAt: number | undefined, now: number): boolean {
  return !lastSentAt || now - lastSentAt >= TYPING_INTERVAL_MS;
}
